import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { apiErr, resolveUrl } from "@/lib/apiClient";
import GoogleSignInButton from "@/components/GoogleSignInButton";

const field = "w-full bg-transparent border-b border-[#E5DFD3] rounded-none px-0 py-3 text-[#1A201D] placeholder:text-[#9A8C73]/60 focus:border-[#2C3531] focus:outline-none transition-colors";

export default function BusinessLogin() {
  const { login } = useAuth();
  const { site } = useSite();
  const settings = site?.settings;
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "super_admin" ? "/admin/super" : u.role === "salon_admin" ? "/admin/salon" : "/business");
    } catch (err) { toast.error(apiErr(err, "Login failed")); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C3531]/90 via-[#2C3531]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 text-[#FDFBF7]">
          <div className="flex items-center gap-2 mb-3">
            {settings?.logo_url ? <img src={resolveUrl(settings.logo_url)} alt="" className="h-8" /> :
              <><Sparkles className="w-6 h-6" strokeWidth={1.75} /><span className="font-display text-2xl font-semibold">{settings?.site_name || "Grow Your Bookings"}</span></>}
          </div>
          <p className="text-[#FDFBF7]/80 max-w-xs">Run your mobile aesthetic business — recruit host salons, manage technicians and fill every slot.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#FDFBF7] grain relative">
        <div className="w-full max-w-sm relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4A5550] hover:text-[#2C3531] mb-10" data-testid="business-login-back"><ArrowLeft className="w-4 h-4" /> Home</Link>
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} data-testid="business-login-form">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-3">Business portal</p>
            <h1 className="font-display text-4xl font-semibold text-[#1A201D] mb-2">Welcome back</h1>
            <p className="text-sm text-[#4A5550] mb-9">Sign in to manage your business.</p>
            <div className="space-y-6">
              <input data-testid="business-login-email" type="email" className={field} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input data-testid="business-login-password" type="password" className={field} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading} data-testid="business-login-submit"
              className="w-full mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2C3531] text-[#FDFBF7] font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </button>
            <div className="flex items-center gap-3 my-6">
              <span className="h-px flex-1 bg-[#E5DFD3]" /><span className="text-xs text-[#9A8C73]">or</span><span className="h-px flex-1 bg-[#E5DFD3]" />
            </div>
            <GoogleSignInButton testid="business-google-signin" />
            <p className="text-center text-sm text-[#4A5550] mt-6">Want to join? <Link to="/register" className="text-[#2C3531] font-semibold underline">Apply as a business</Link></p>
          </motion.form>
        </div>
      </div>
    </div>
  );
}
